import { cloneDeep, snakeCase } from "lodash-es";

import { streamTypes } from "shared/constants";
import DateRange from "shared/helpers/DateRange";
import {
  filterSocialPlatformsByTypes,
  getSources,
  type Medium,
  MediumField,
  type MediumName,
  social,
  type SocialPlatform,
} from "shared/helpers/media";
import {
  type Source,
  sourceGroupKeys,
  sourceKeysFor,
} from "shared/helpers/sourceFilters";
import type { SimpleBoolean } from "shared/resources/Stream";
import features from "shared/services/features";
import type { Maybe, Nullable, Stream } from "shared/types";
import { isNonNullable } from "shared/types/guards";
import type {
  MentionType,
  ShareType,
  SocialMentionType,
} from "shared/types/mentions";

import {
  SortOptionField,
  SortOptionMissing,
  SortOptionOrder,
} from "./mentions";

export type StreamFiltersTag = {
  color: Nullable<string>;
  focus?: boolean;
  id: number;
  label: string;
  selected?: boolean;
};

export interface StreamFiltersSortOption {
  missing: SortOptionMissing;
  orderBy: SortOptionOrder;
  sortBy: SortOptionField;
}

export type StreamFiltersAuthor = {
  focus?: boolean;
  id: number;
  name: string;
};

export type StreamFiltersAuthorValue = StreamFiltersAuthor | string;

export type StreamFiltersCategory = "National" | "Metro" | "Regional";

export type StreamFiltersLicense = "nla" | "cla";

export type StreamFiltersMinMax = {
  max: Nullable<number>;
  min: Nullable<number>;
};

export interface ImpactScoreOption {
  key: CheckboxColor;
  label?: string;
  max: number;
  min: number;
}

type CheckboxColor =
  | "low"
  | "negative"
  | "average"
  | "trending_negative"
  | "good"
  | "strong"
  | "trending_positive"
  | "excellent"
  | "positive"
  | "primary"; // default

export const IMPACT_SCORE_OPTIONS: ImpactScoreOption[] = [
  {
    key: "low",
    max: 200,
    min: 0,
  },
  {
    key: "average",
    max: 350,
    min: 200.01,
  },
  {
    key: "excellent",
    max: 3000,
    min: 350.01,
  },
];

export type StreamFiltersFrontPageRanks = {
  min: number;
};

export type StreamFiltersTier = {
  label: string;
  value: number;
};

type StreamTierFilters = {
  [key in `${MediumField}_tiers`]?: number[];
};

export interface StreamRequestFiltersSortOption {
  missing: SortOptionMissing;
  sort_by: SortOptionField;
  sort_order: SortOptionOrder;
}

interface StreamBaseFilters extends StreamTierFilters {
  authors?: string;
  boolean_query?: string;
  category_keys?: string[];
  cluster_topic_ids?: number[];
  domain_authority?: StreamFiltersMinMax[];
  excluded_authors?: string;
  excluded_cluster_topic_ids?: number[];
  excluded_keywords?: string;
  excluded_language_codes?: string[];
  excluded_license?: StreamFiltersLicense[];
  excluded_location_keys?: string[];
  impact_score?: StreamFiltersMinMax[];
  include_external_items?: boolean;
  include_international_plus?: boolean;
  include_newswire_content?: boolean;
  include_online_cald_sources?: string;
  include_print_cald_sources?: string;
  keywords?: string;
  language_codes?: string[];
  license?: StreamFiltersLicense[];
  location_keys?: string[];
  max_advertisement_rate?: number;
  max_aggregate_emotionality?: number;
  max_aggregate_fakeness?: number;
  max_aggregate_harmful?: number;
  max_aggregate_spam?: number;
  max_audience?: number;
  max_desktop_monthly_visits?: number;
  max_mobile_monthly_visits?: number;
  max_react_score?: number;
  max_social_impact_threshold?: number;
  max_word_count?: number;
  media?: MediumName[];
  min_advertisement_rate?: number;
  min_aggregate_emotionality?: number;
  min_aggregate_fakeness?: number;
  min_aggregate_harmful?: number;
  min_aggregate_spam?: number;
  min_audience?: number;
  min_desktop_monthly_visits?: number;
  min_mobile_monthly_visits?: number;
  min_react_score?: number;
  min_social_impact_threshold?: number;
  min_word_count?: number;
  only_external_items?: boolean;
  page_numbers?: StreamFiltersMinMax;
  referred_urls_cache?: string;
  selected_radio_content?: MentionType[];
  selected_social_content?: SocialMentionType[];
  selected_tv_content?: MentionType[];
  sentiment?: StreamFiltersMinMax[];
  share_types?: ShareType[];
  social_only_verified?: boolean;
  unrated_sentiment?: boolean;
}

export interface StreamRequestFilters extends StreamBaseFilters {
  bundle_broadcast?: boolean;
  categories?: StreamFiltersCategory[];
  collapse_syndicated?: boolean;
  excluded_source_group_keys?: string[];
  excluded_source_keys?: string[];
  excluded_tag_ids?: number[];
  front_page_ranks?: StreamFiltersFrontPageRanks;
  include_bookmark_syndication?: boolean;
  lastSavedEnabled?: boolean;
  location_ids?: number[];
  max_domain_authority?: number;
  min_domain_authority?: number;
  minimum_follower_count?: number;
  online_tiers?: number[];
  programs?: string[];
  range?: DateRange;
  referred_urls_cache?: string;
  social_impact_threshold?: number;
  sort_options?: StreamRequestFiltersSortOption[];
  source_group_keys?: string[];
  source_keys?: string[];
  stock_symbol?: string;
  tag_ids?: number[];
  types?: (MentionType | SocialMentionType)[];
  use_exact_authors?: boolean;
  verified?: boolean;
}

export type InitialStreamFilters<
  TAuthor extends StreamFiltersAuthorValue = StreamFiltersAuthorValue,
> = Partial<Omit<StreamFilters<TAuthor>, "stream">>;

export enum StreamFiltersType {
  Base = "base",
  Filters = "filters",
  Report = "report",
  Stream = "stream",
}

type StreamFiltersTypes<T> = T extends StreamFiltersType.Filters
  ? StreamRequestFilters
  : T extends StreamFiltersType.Report
    ? StreamReportFilters
    : T extends StreamFiltersType.Stream
      ? StreamOptionsFilters
      : never;

type StreamOptionTypes<T> = T extends StreamFiltersType.Report
  ? StreamReportFilters
  : T extends StreamFiltersType.Stream
    ? StreamOptionsFilters
    : never;

export interface StreamOptionsFilters extends StreamBaseFilters {
  authors_filter?: StreamFiltersAuthor[];
  bookmark_stream_ids?: number[];
  boolean_query?: StreamFilters["booleanQuery"];
  excluded_authors_filter?: StreamFiltersAuthor[];
  excluded_bookmark_stream_ids?: number[];
  minimum_follower_count?: number;
  simple_boolean?: StreamFilters["simpleBoolean"];
  types?: (MentionType | SocialMentionType)[];
  verified?: boolean;
}

export interface StreamReportFilters extends StreamOptionsFilters {
  collapse_syndicated?: boolean;
  excluded_source_group_keys?: string[];
  excluded_source_keys?: string[];
  source_group_keys?: string[];
  source_keys?: string[];
}

/**
 * Input type for StreamFilters.fromOptions().
 * Accepts report-style filters (with bookmark_stream_ids) and also
 * request-style tag fields (tag_ids, excluded_tag_ids) for flexibility.
 */
export type StreamFiltersFromOptionsInput = Pick<
  StreamRequestFilters,
  "tag_ids" | "excluded_tag_ids"
> &
  StreamReportFilters;

function isStreamFilters(
  type: StreamFiltersType,
  params: unknown
): params is StreamOptionsFilters {
  return StreamFiltersType.Stream === type;
}

function isStreamReportFilters(
  type: StreamFiltersType,
  params: unknown
): params is StreamReportFilters {
  return StreamFiltersType.Report === type;
}

function isStreamRequestFilters(
  type: StreamFiltersType,
  params: unknown
): params is StreamRequestFilters {
  return StreamFiltersType.Filters === type;
}

function isStreamFiltersAuthor(
  author: StreamFiltersAuthorValue
): author is StreamFiltersAuthor {
  return typeof author === "object" && "id" in author && "name" in author;
}

function authorNamesFromString(authors: string): string[] {
  return authors
    .split(",")
    .map((author) => author.trim())
    .filter(Boolean);
}

export default class StreamFilters<
  TAuthor extends StreamFiltersAuthorValue = StreamFiltersAuthorValue,
> {
  static get DEFAULT_FILTERS(): InitialStreamFilters {
    const streamFilters = new StreamFilters(null);

    return streamFilters.initialFilters;
  }

  advertisementRate: StreamFiltersMinMax;

  aggregateEmotionality: StreamFiltersMinMax;

  aggregateFakeness: StreamFiltersMinMax;

  aggregateHarmful: StreamFiltersMinMax;

  aggregateSpam: StreamFiltersMinMax;

  audience: StreamFiltersMinMax;

  authors: TAuthor[];

  booleanQuery: string;

  bundleBroadcast: boolean;

  categories: StreamFiltersCategory[];

  categoryKeys: string[];

  clusterTopicIds: number[];

  collapseSyndicated: boolean;

  dateRangeEnabled: boolean;

  desktopMonthlyVisits: StreamFiltersMinMax;

  domainAuthority: StreamFiltersMinMax;

  domainAuthorityRanges: StreamFiltersMinMax[];

  excludedAuthors: TAuthor[];

  excludedClusterTopicIds: number[];

  excludedKeywords: string;

  excludedLanguageCodes: string[];

  excludedLicense: StreamFiltersLicense[];

  excludedLocationKeys: string[];

  excludedSourceGroupKeys: string[];

  excludedSourceKeys: string[];

  excludedSources: Source[];

  excludedTags: StreamFiltersTag[];

  externalItemsEnabled: boolean;

  frontPageRanks: StreamFiltersFrontPageRanks;

  frontPageRanksEnabled: boolean;

  impactScoreRanges: StreamFiltersMinMax[];

  includeBookmarkSyndication: boolean;

  includedMentionsEnabled: boolean;

  includeInternationalPlus: Nullable<boolean>;

  includeNewswireContent: boolean;

  includeOnlineCaldSources: string;

  includePrintCaldSources: string;

  initialFilters: InitialStreamFilters;

  keywords: string;

  languageCodes: string[];

  lastSavedEnabled: boolean;

  license: StreamFiltersLicense[];

  locationKeys: string[];

  locations: number[];

  media: MediumName[];

  mediaTypes: MentionType[];

  minimumFollowerCount: number;

  minimumFollowerCountEnabled: boolean;

  mobileMonthlyVisits: StreamFiltersMinMax;

  onlyShowExternalItems: boolean;

  pageNumbers: StreamFiltersMinMax;

  pageNumbersEnabled: boolean;

  platformOrder: SocialPlatform[];

  platforms: SocialPlatform[];

  programs: string[];

  range: DateRange;

  reactScore: StreamFiltersMinMax;

  reactScoreAverageSelected: boolean;

  reactScoreBreakdown: boolean;

  referredUrlsCache: Nullable<string> | undefined;

  selectedRadioContent: MentionType[];

  selectedSocialContent: SocialMentionType[];

  selectedTvContent: MentionType[];

  sentiment: StreamFiltersMinMax[];

  sentimentEnabled: boolean;

  shareTypes: ShareType[];

  simpleBoolean: Nullable<SimpleBoolean>;

  socialImpactThreshold: StreamFiltersMinMax;

  socialOnlyVerified: Nullable<boolean>;

  sortOptions: StreamFiltersSortOption[];

  sourceGroupKeys: string[];

  sourceKeys: string[];

  sources: Source[];

  stockSymbol?: string;

  stream: Nullable<Stream>;

  tags: StreamFiltersTag[];

  tiers: StreamFiltersTier[];

  unratedSentiment: boolean;

  useExactAuthors: boolean;

  verifiedEnabled: boolean;

  wordCount: StreamFiltersMinMax;

  wordCountEnabled: boolean;

  constructor(
    stream: Nullable<Stream>,
    initialFilters: InitialStreamFilters = {}
  ) {
    this.advertisementRate = { max: null, min: null };
    this.aggregateEmotionality = { max: 100, min: 0 };
    this.aggregateFakeness = { max: 100, min: 0 };
    this.aggregateHarmful = { max: 100, min: 0 };
    this.aggregateSpam = { max: 100, min: 0 };
    this.audience = { max: null, min: null };
    this.authors = [];
    this.booleanQuery = "";
    this.bundleBroadcast = false;
    this.categories = [];
    this.categoryKeys = [];
    this.clusterTopicIds = [];
    this.collapseSyndicated = false;
    this.dateRangeEnabled = false;
    this.desktopMonthlyVisits = { max: null, min: null };
    this.domainAuthority = { max: null, min: null };
    this.domainAuthorityRanges = [];
    this.excludedAuthors = [];
    this.excludedClusterTopicIds = [];
    this.excludedLicense = [];
    this.excludedKeywords = "";
    this.excludedLanguageCodes = [];
    this.excludedLocationKeys = [];
    this.excludedSourceGroupKeys = [];
    this.excludedSourceKeys = [];
    this.excludedSources = [];
    this.excludedTags = [];
    this.frontPageRanks = { min: 0 };
    this.frontPageRanksEnabled = false;
    this.impactScoreRanges = [];
    this.includeBookmarkSyndication = false;
    this.includedMentionsEnabled = false;
    this.includeInternationalPlus = null;
    this.includeOnlineCaldSources = "";
    this.includePrintCaldSources = "";
    this.keywords = "";
    this.languageCodes = [];
    this.lastSavedEnabled = false;
    this.license = [];
    this.locationKeys = [];
    this.locations = [];
    this.media = [];
    this.mediaTypes = [];
    this.minimumFollowerCount = 500;
    this.minimumFollowerCountEnabled = false;
    this.mobileMonthlyVisits = { max: null, min: null };

    this.includeNewswireContent =
      stream?.options?.include_newswire_content ?? false;

    this.onlyShowExternalItems = false;
    this.pageNumbers = { max: null, min: null };
    this.pageNumbersEnabled = false;
    this.platformOrder = [];
    this.platforms = [];
    this.programs = [];
    this.range = DateRange.today();
    this.reactScore = { max: 100, min: 0 };
    this.reactScoreAverageSelected = true;
    this.reactScoreBreakdown = false;
    this.selectedSocialContent = [];
    this.selectedRadioContent = [];
    this.selectedTvContent = [];
    this.sentiment = [];
    this.sentimentEnabled = false;
    this.shareTypes = [];
    this.simpleBoolean = null;
    this.socialImpactThreshold = { max: null, min: null };
    this.socialOnlyVerified = null;
    this.referredUrlsCache = null;

    this.sortOptions = [
      {
        missing: SortOptionMissing.LAST,
        orderBy: SortOptionOrder.DESC,
        sortBy: SortOptionField.TIMESTAMP,
      },
    ];

    this.sourceGroupKeys = [];
    this.sourceKeys = [];
    this.sources = [];
    this.stream = stream;
    this.tags = [];
    this.tiers = [];
    this.unratedSentiment = false;
    this.useExactAuthors = false;
    this.verifiedEnabled = false;
    this.wordCount = { max: null, min: null };
    this.wordCountEnabled = false;

    if (
      this.canFilterExternalItems() &&
      !("externalItemsEnabled" in initialFilters)
    ) {
      this.externalItemsEnabled = true;
    }

    Object.assign(this, initialFilters);

    const streamFiltersClone = cloneDeep(this);

    const {
      initialFilters: initialFiltersClone,
      stream: streamClone,
      ...filters
    } = streamFiltersClone;

    this.initialFilters = cloneDeep(filters);
  }

  static fromOptions(
    streamOptions: StreamFiltersFromOptionsInput,
    stream: Nullable<Stream>,
    initialFilters: InitialStreamFilters = {},
    tagResolver?: (id: number) => Maybe<StreamFiltersTag>
  ): StreamFilters {
    const {
      authors: authorsString = "",
      authors_filter: authorsFilter = [],
      boolean_query: booleanQuery = "",
      category_keys: categoryKeys = [],
      cluster_topic_ids: clusterTopicIds = [],
      collapse_syndicated: collapseSyndicated = false,
      domain_authority: domainAuthorityRanges = [],
      excluded_authors: excludedAuthorsString = "",
      excluded_authors_filter: excludedAuthorsFilter = [],
      excluded_cluster_topic_ids: excludedClusterTopicIds = [],
      excluded_keywords: excludedKeywords = "",
      excluded_language_codes: excludedLanguageCodes = [],
      excluded_license: excludedLicense = [],
      excluded_location_keys: excludedLocationKeys = [],
      excluded_source_group_keys: excludedSourceGroupKeys = [],
      excluded_source_keys: excludedSourceKeys = [],
      impact_score: impactScoreRanges = [],
      include_external_items: externalItemsEnabled,
      include_international_plus: includeInternationalPlus = null,
      include_newswire_content: includeNewswireContent = stream?.options
        ?.include_newswire_content ?? false,
      include_online_cald_sources: includeOnlineCaldSources = "",
      include_print_cald_sources: includePrintCaldSources = "",
      keywords = "",
      language_codes: languageCodes = [],
      license = [],
      location_keys: locationKeys = [],
      max_advertisement_rate: maxAdvertisementRate = null,
      max_aggregate_emotionality: maxAggregateEmotinality,
      max_aggregate_fakeness: maxAggregateFakeness,
      max_aggregate_harmful: maxAggregateHarmful,
      max_aggregate_spam: maxAggregateSpam,
      max_audience: maxAudience = null,
      max_desktop_monthly_visits: maxDesktopMonthlyVisits = null,
      max_mobile_monthly_visits: maxMobileMonthlyVisits = null,
      max_react_score: maxReactScore = 1,
      max_social_impact_threshold: maxSocialImpactThreshold = null,
      max_word_count: maxWordCount = null,
      media = [],
      min_advertisement_rate: minAdvertisementRate = null,
      min_aggregate_emotionality: minAggregateEmotionality,
      min_aggregate_fakeness: minAggregateFakeness,
      min_aggregate_harmful: minAggregateHarmful,
      min_aggregate_spam: minAggregateSpam,
      min_audience: minAudience = null,
      min_desktop_monthly_visits: minDesktopMonthlyVisits = null,
      min_mobile_monthly_visits: minMobileMonthtlyVisits = null,
      min_react_score: minReactScore = 0,
      min_social_impact_threshold: minSocialImpactThreshold = null,
      min_word_count: minWordCount = null,
      minimum_follower_count: minimumFollowerCount,
      only_external_items: onlyShowExternalItems = false,
      page_numbers: pageNumbers = { max: null, min: null },
      selected_radio_content: selectedRadioContent = [],
      selected_social_content: selectedSocialContent = [],
      selected_tv_content: selectedTvContent = [],
      sentiment = [],
      share_types: shareTypes = [],
      simple_boolean: simpleBoolean = null,
      social_only_verified: socialOnlyVerified = null,
      source_group_keys: persistedSourceGroupKeys = [],
      source_keys: persistedSourceKeys = [],
      types = [],
      unrated_sentiment: unratedSentiment = false,
      verified: verifiedEnabled = false,
      // Tag IDs - used in different contexts (filters vs reports)
      bookmark_stream_ids: bookmarkStreamIds = [],
      excluded_bookmark_stream_ids: excludedBookmarkStreamIds = [],
      excluded_tag_ids: excludedTagIds = [],
      tag_ids: tagIds = [],
    } = streamOptions;

    const authors = authorsFilter.length
      ? authorsFilter
      : authorNamesFromString(authorsString);

    const excludedAuthors = excludedAuthorsFilter.length
      ? excludedAuthorsFilter
      : authorNamesFromString(excludedAuthorsString);

    // Resolve tag IDs to full tag objects
    const resolveTagIds = (ids: number[]): StreamFiltersTag[] => {
      if (!tagResolver || !ids.length) return [];

      return ids
        .map((id) => tagResolver(id))
        .filter((tag): tag is StreamFiltersTag => tag !== undefined);
    };

    // Combine tag IDs from both formats (filters use tag_ids, reports use bookmark_stream_ids)
    const allTagIds = [...tagIds, ...bookmarkStreamIds];
    const allExcludedTagIds = [...excludedTagIds, ...excludedBookmarkStreamIds];

    const tags = resolveTagIds(allTagIds);
    const excludedTags = resolveTagIds(allExcludedTagIds);

    const pageNumbersEnabled = Boolean(
      pageNumbers !== undefined &&
      (pageNumbers.min !== null || pageNumbers.max !== null)
    );

    const wordCountEnabled = Boolean(
      minWordCount !== null || maxWordCount !== null
    );

    const reactScoreAverageSelected = Boolean(
      maxAggregateEmotinality === undefined &&
      maxAggregateFakeness === undefined &&
      maxAggregateHarmful === undefined &&
      maxAggregateSpam === undefined
    );

    // Convert types (content type strings) back to platforms
    const platforms: SocialPlatform[] = types.length
      ? filterSocialPlatformsByTypes(types).map(({ platform }) => platform)
      : [];

    // Derive enabled flags from presence of values
    const minimumFollowerCountEnabled = minimumFollowerCount !== undefined;

    return new StreamFilters(stream, {
      ...initialFilters,
      advertisementRate: {
        max: maxAdvertisementRate !== null ? maxAdvertisementRate / 100 : null,
        min: minAdvertisementRate !== null ? minAdvertisementRate / 100 : null,
      },
      aggregateEmotionality: {
        max: maxAggregateEmotinality ? maxAggregateEmotinality * 100 : 100,
        min: minAggregateEmotionality ? minAggregateEmotionality * 100 : 0,
      },
      aggregateFakeness: {
        max: maxAggregateFakeness ? maxAggregateFakeness * 100 : 100,
        min: minAggregateFakeness ? minAggregateFakeness * 100 : 0,
      },
      aggregateHarmful: {
        max: maxAggregateHarmful ? maxAggregateHarmful * 100 : 100,
        min: minAggregateHarmful ? minAggregateHarmful * 100 : 0,
      },
      aggregateSpam: {
        max: maxAggregateSpam ? maxAggregateSpam * 100 : 100,
        min: minAggregateSpam ? minAggregateSpam * 100 : 0,
      },
      audience: { max: maxAudience, min: minAudience },
      authors,
      booleanQuery,
      categoryKeys,
      clusterTopicIds,
      collapseSyndicated,
      desktopMonthlyVisits: {
        max: maxDesktopMonthlyVisits,
        min: minDesktopMonthlyVisits,
      },
      domainAuthorityRanges,
      excludedAuthors,
      excludedClusterTopicIds,
      excludedKeywords,
      excludedLanguageCodes,
      excludedLicense,
      excludedLocationKeys,
      excludedSourceGroupKeys,
      excludedSourceKeys,
      ...(externalItemsEnabled !== undefined ? { externalItemsEnabled } : {}),
      excludedTags,
      impactScoreRanges,
      includeInternationalPlus,
      includeOnlineCaldSources,
      includePrintCaldSources,
      keywords,
      languageCodes,
      license,
      locationKeys,
      media,
      ...(minimumFollowerCount !== undefined ? { minimumFollowerCount } : {}),
      includeNewswireContent,
      minimumFollowerCountEnabled,
      mobileMonthlyVisits: {
        max: maxMobileMonthlyVisits,
        min: minMobileMonthtlyVisits,
      },
      onlyShowExternalItems,
      pageNumbers,
      pageNumbersEnabled,
      platforms,
      reactScore: {
        max: maxReactScore ? maxReactScore * 100 : 100,
        min: minReactScore ? minReactScore * 100 : 0,
      },
      reactScoreAverageSelected,
      selectedRadioContent,
      selectedSocialContent,
      selectedTvContent,
      sentiment,
      sentimentEnabled: Boolean(sentiment.length),
      shareTypes,
      simpleBoolean,
      socialImpactThreshold: {
        max: maxSocialImpactThreshold,
        min: minSocialImpactThreshold,
      },
      socialOnlyVerified,
      sourceGroupKeys: persistedSourceGroupKeys,
      sourceKeys: persistedSourceKeys,
      tags,
      unratedSentiment,
      verifiedEnabled,
      wordCount: { max: maxWordCount, min: minWordCount },
      wordCountEnabled,
    });
  }

  appliedFilters(): string[] {
    const filtersUsed: string[] = [];

    if (this.keywordsFiltersCount()) filtersUsed.push("Keywords");
    filtersUsed.push(...this.mediaFilters());
    filtersUsed.push(...this.sourceFilters());
    filtersUsed.push(...this.contentFilters());
    if (this.dateRangeFiltersCount()) filtersUsed.push("Date Range");
    filtersUsed.push(...this.metricFilters());
    filtersUsed.push(...this.otherFilters());
    filtersUsed.push(...this.reportsFilters());

    return filtersUsed;
  }

  canFilterExternalItems(): boolean {
    if (this.stream === null) {
      return false;
    }

    return (
      this.supportsExternalItems(this.stream) &&
      features.has("has_external_items")
    );
  }

  clone(): StreamFilters<TAuthor> {
    return Object.assign(
      new StreamFilters<TAuthor>(this.stream, cloneDeep(this)),
      {
        initialFilters: cloneDeep(this.initialFilters),
      }
    );
  }

  contentFilters(): string[] {
    const filters: string[] = [];

    if (this.authors.length) filters.push("Authors");
    if (this.excludedAuthors.length) filters.push("Excluded Authors");
    if (this.excludedTags.length) filters.push("Excluded Tags");
    if (this.languageCodes.length) filters.push("Language Codes");
    if (this.excludedLanguageCodes.length)
      filters.push("Exclude Language Codes");
    if (this.license.length) filters.push("Content Types");
    if (this.pageNumbersEnabled) filters.push("Page Number");
    if (this.tags.length) filters.push("Tags");
    if (this.wordCountEnabled) filters.push("Word Count");

    return filters;
  }

  contentFiltersCount(): number {
    let count = 0;

    if (this.authors.length) count += 1;
    if (this.excludedAuthors.length) count += 1;
    if (this.excludedTags.length) count += 1;
    if (this.languageCodes.length) count += 1;
    if (this.excludedLanguageCodes.length) count += 1;
    if (this.license.length) count += 1;
    if (this.pageNumbersEnabled) count += 1;
    if (this.tags.length) count += 1;
    if (this.wordCountEnabled) count += 1;

    return count;
  }

  dateRangeFiltersCount(): number {
    if (this.dateRangeEnabled) {
      return 1;
    }

    return 0;
  }

  enabledMedia(): Medium[] {
    const sources = getSources();

    if (features.has(social.featureFlag)) {
      const hasSocial = sources.find(({ medium }) => medium === social.medium);

      if (!hasSocial) {
        sources.push(social);
      }
    }

    return sources.filter(({ medium }) =>
      this.stream!.enabled_media.includes(medium)
    );
  }

  filteringAuthors(): boolean {
    return Boolean(this.authors.length);
  }

  filteringContent(
    { ignoredFilters }: { ignoredFilters: string[] } = { ignoredFilters: [] }
  ): boolean {
    const contentFilters = {
      dateRange:
        this.initialFilters.range &&
        !this.range.equals(this.initialFilters.range),
      dateRangeEnabled:
        this.dateRangeEnabled !== this.initialFilters.dateRangeEnabled,
      excludedKeywords: this.excludedKeywords && this.excludedKeywords !== "",
      keywords: this.keywords && this.keywords !== "",
      lastSaved: this.lastSavedEnabled,
      pageNumber:
        this.pageNumbersEnabled !== this.initialFilters.pageNumbersEnabled,
      sentiment:
        this.sentimentEnabled !== this.initialFilters?.sentimentEnabled,
      wordCount: this.wordCountEnabled !== this.initialFilters.wordCountEnabled,
    };

    return Object.entries(contentFilters).some(
      ([filter, active]) => !ignoredFilters.includes(filter) && active
    );
  }

  filteringHandleOrSocialImpact(): boolean {
    const filters = [
      this.authors.length,
      this.verifiedEnabled !== this.initialFilters.verifiedEnabled,
      this.minimumFollowerCountEnabled !==
        this.initialFilters.minimumFollowerCountEnabled,
    ];

    return filters.some((filter) => filter);
  }

  filteringKeywords(): boolean {
    if (this.booleanQuery || this.keywords || this.excludedKeywords)
      return true;

    return false;
  }

  filteringLocations(): boolean {
    return Boolean(this.locations.length);
  }

  filteringMedia(defaultMedia: MediumName[]): boolean {
    return this.media.length
      ? defaultMedia.length - this.media.length > 0
      : false;
  }

  filteringPlatforms(): boolean {
    return Boolean(this.platforms.length);
  }

  filteringReactScore(): boolean {
    if (this.reactScoreBreakdown) {
      return true;
    }

    const reactScoreFilters = [
      this.reactScoreAverageSelected && this.scoreChanged(this.reactScore),
      !this.reactScoreAverageSelected &&
        this.scoreChanged(this.aggregateEmotionality),
      !this.reactScoreAverageSelected &&
        this.scoreChanged(this.aggregateFakeness),
      !this.reactScoreAverageSelected && this.scoreChanged(this.aggregateSpam),
      !this.reactScoreAverageSelected &&
        this.scoreChanged(this.aggregateHarmful),
    ];

    return reactScoreFilters.some((filter) => filter);
  }

  filteringSortOptions(): boolean {
    return !this.sortOptions.some(
      (sortOption) =>
        sortOption.sortBy === SortOptionField.TIMESTAMP &&
        sortOption.orderBy === SortOptionOrder.DESC
    );
  }

  filteringSources(): boolean {
    const sourceFilters = [
      this.sources.length,
      this.excludedSources.length,
      this.categories.length,
      this.tiers?.length,
    ];

    return sourceFilters.some((filter) => filter);
  }

  filteringTags(): boolean {
    const tagFilters = [this.tags.length, this.excludedTags.length];

    return tagFilters.some((filter) => filter);
  }

  keywordsFiltersCount(): number {
    let count = 0;
    if (this.booleanQuery) count += 1;
    if (this.keywords || this.excludedKeywords) count += 1;

    return count;
  }

  mediaCoverageFilters(medium: Medium): string[] {
    const filters: string[] = [];

    if (this.locationKeys.some((key) => key.includes(medium.field)))
      filters.push(`${medium.medium} Location`);

    if (this.excludedLocationKeys.some((key) => key.includes(medium.field)))
      filters.push(`Excluded ${medium.medium} Location`);

    if (this.categoryKeys.some((key) => key.includes(medium.field)))
      filters.push(`${medium.medium} Category`);

    if (medium.field === MediumField.ONLINE) {
      if (this.domainAuthorityRanges.length) filters.push("Domain Authority");

      if (this.includeInternationalPlus !== null)
        filters.push("International Plus");

      if (this.includeOnlineCaldSources && features.has("has_cald_sources"))
        filters.push("Online CALD Sources");
    }

    if (medium.field === MediumField.PRINT) {
      if (this.includePrintCaldSources && features.has("has_cald_sources"))
        filters.push("Print CALD Sources");
    }

    if (medium.field === MediumField.SOCIAL) {
      if (this.selectedSocialContent.length) filters.push("Social Content");

      if (this.validateMinOrMax(this.socialImpactThreshold))
        filters.push("Social Impact Threshold");

      if (
        this.socialOnlyVerified !== null &&
        this.stream?.social_only_verified !== this.socialOnlyVerified
      ) {
        filters.push("Social Verified Only");
      }
    }

    if (medium.field === MediumField.TV) {
      if (this.selectedTvContent.length) filters.push("TV Content");
    }

    if (medium.field === MediumField.RADIO) {
      if (this.selectedRadioContent.length) filters.push("Radio Content");
    }

    return filters;
  }

  mediaFilters(): string[] {
    if (this.stream && this.stream.enabled_media) {
      const filters: string[] = [];

      this.enabledMedia().forEach((medium) => {
        if (this.media.includes(medium.medium)) {
          filters.push(medium.medium);
        }

        filters.push(...this.mediaCoverageFilters(medium));
      });

      return filters;
    }

    return this.media;
  }

  mediaFiltersCount(): number {
    if (this.stream && this.stream.enabled_media) {
      if (
        this.media.length &&
        this.enabledMedia().length !== this.media.length
      ) {
        return this.media.length;
      }
    }

    return 0;
  }

  merge(filters: InitialStreamFilters) {
    return Object.assign(this, filters);
  }

  metricFilters(): string[] {
    const filters: string[] = [];

    if (this.filteringReactScore()) filters.push("React Score");
    if (this.validateMinOrMax(this.advertisementRate))
      filters.push("Advertisement Rate");
    if (this.sentimentEnabled || this.unratedSentiment)
      filters.push("Sentiment");
    if (this.impactScoreRanges.length) filters.push("Impact Score");
    if (this.validateMinOrMax(this.audience)) filters.push("Audience");
    if (this.validateMinOrMax(this.mobileMonthlyVisits))
      filters.push("Mobile Monthly Visits");
    if (this.validateMinOrMax(this.desktopMonthlyVisits))
      filters.push("Desktop Monthly Visits");

    return filters;
  }

  metricFiltersCount(): number {
    let count = 0;

    if (this.filteringReactScore()) count += 1;
    if (this.validateMinOrMax(this.advertisementRate)) count += 1;
    if (this.sentimentEnabled || this.unratedSentiment) count += 1;
    if (this.impactScoreRanges.length) count += 1;

    if (
      this.validateMinOrMax(this.audience) ||
      this.validateMinOrMax(this.mobileMonthlyVisits) ||
      this.validateMinOrMax(this.desktopMonthlyVisits)
    ) {
      count += 1;
    }

    return count;
  }

  otherFilters(): string[] {
    const filters: string[] = [];

    if (this.canFilterExternalItems()) {
      if (!this.externalItemsEnabled) filters.push("Exclude External Items");
      if (this.onlyShowExternalItems) filters.push("Only show External Items");
    }

    if (this.includeNewswireContent) filters.push("Display Newswire Content");

    return filters;
  }

  otherFiltersCount(): number {
    let count = 0;

    if (
      this.canFilterExternalItems() &&
      (!this.externalItemsEnabled || this.onlyShowExternalItems)
    ) {
      count += 1;
    }

    if (this.includeNewswireContent) count += 1;

    return count;
  }

  platformFiltersCount(): number {
    return this.platforms.length;
  }

  platformsToMediaTypes(platforms: SocialPlatform[]): SocialMentionType[] {
    const mediaTypes: SocialMentionType[] = [];

    social.platforms.forEach((platform) => {
      if (platforms.includes(platform.platform)) {
        mediaTypes.push(...platform.fields);
      }
    });

    return mediaTypes;
  }

  rangeEquals(
    selected: StreamFiltersMinMax,
    initial: StreamFiltersMinMax
  ): boolean {
    return selected.min === initial.min && selected.max === initial.max;
  }

  reportsFilters(): string[] {
    const filters: string[] = [];

    if (this.includedMentionsEnabled) filters.push("Included Mentions");
    if (this.sortOptions.length > 1) filters.push("Sort Options");
    if (this.tiers.length) filters.push("Tiers");
    if (this.lastSavedEnabled) filters.push("Last Saved");

    return filters;
  }

  reportsFiltersCount(): number {
    let count = 0;
    if (this.includedMentionsEnabled) count += 1;
    if (this.sortOptions.length > 1) count += 1;
    if (this.tiers.length) count += 1;
    if (this.lastSavedEnabled) count += 1;

    return count;
  }

  requestFilters<T extends StreamFiltersType = StreamFiltersType.Filters>(
    type: T = StreamFiltersType.Filters as T
  ): StreamFiltersTypes<T> {
    const params = <StreamFiltersTypes<T>>{};

    if (this.keywords !== "") params.keywords = this.keywords;
    if (this.excludedKeywords !== "")
      params.excluded_keywords = this.excludedKeywords;
    if (this.booleanQuery !== "") params.boolean_query = this.booleanQuery;

    if (
      isStreamReportFilters(type, params) ||
      isStreamRequestFilters(type, params)
    ) {
      const includedSourceGroupKeys = this.sources.length
        ? sourceGroupKeys(this.sources)
        : this.sourceGroupKeys;

      const includedSourceKeys = this.sources.length
        ? sourceKeysFor(this.sources)
        : this.sourceKeys;

      if (includedSourceKeys.length) {
        params.source_keys = includedSourceKeys;
      }

      if (includedSourceGroupKeys.length) {
        params.source_group_keys = includedSourceGroupKeys;
      }

      const excludedSourceGroupKeys = this.excludedSources.length
        ? sourceGroupKeys(this.excludedSources)
        : this.excludedSourceGroupKeys;

      const excludedSourceKeys = this.excludedSources.length
        ? sourceKeysFor(this.excludedSources)
        : this.excludedSourceKeys;

      if (excludedSourceKeys.length) {
        params.excluded_source_keys = excludedSourceKeys;
      }

      if (excludedSourceGroupKeys.length) {
        params.excluded_source_group_keys = excludedSourceGroupKeys;
      }
    }

    if (this.tags.length > 0) {
      const tags = this.tags.map((tag) => tag.id);

      if (
        isStreamReportFilters(type, params) ||
        isStreamFilters(type, params)
      ) {
        params.bookmark_stream_ids = tags;
      } else {
        params.tag_ids = tags;
      }
    }

    if (this.excludedTags?.length) {
      const excludedTags = this.excludedTags.map(
        (excludedTag) => excludedTag.id
      );

      if (
        isStreamReportFilters(type, params) ||
        isStreamFilters(type, params)
      ) {
        params.excluded_bookmark_stream_ids = excludedTags;
      } else {
        params.excluded_tag_ids = excludedTags;
      }
    }

    if (isStreamFilters(type, params) || isStreamRequestFilters(type, params)) {
      if (this.platforms.length > 0) {
        params.types = this.platformsToMediaTypes(this.platforms);
      } else if (
        isStreamRequestFilters(type, params) &&
        this.mediaTypes.length > 0
      ) {
        params.types = this.mediaTypes;
      }

      if (this.verifiedEnabled) params.verified = this.verifiedEnabled;

      if (this.minimumFollowerCountEnabled)
        params.minimum_follower_count = this.minimumFollowerCount;
    }

    if (isStreamRequestFilters(type, params)) {
      if (this.useExactAuthors) params.use_exact_authors = this.useExactAuthors;

      if (
        this.domainAuthority &&
        Object.keys(this.domainAuthority).length &&
        this.domainAuthority.min !== null &&
        this.domainAuthority.max !== null
      ) {
        params.min_domain_authority = this.domainAuthority.min;
        params.max_domain_authority = this.domainAuthority.max;
      }

      if (this.stockSymbol) params.stock_symbol = this.stockSymbol;

      if (this.sortOptions.length) {
        const sortOptions = this.sortOptions
          .filter((option) => option.sortBy && option.orderBy)
          .map((option) => ({
            missing: option.missing,
            sort_by: option.sortBy!,
            sort_order: option.orderBy!,
          }));

        if (sortOptions.length > 0) {
          params.sort_options = sortOptions;
        }
      }

      if (this.categories.length) params.categories = this.categories;

      if (this.locations.length) {
        params.location_ids = this.locations;
      }

      if (this.dateRangeEnabled) {
        params.range = this.range;
      }

      if (this.lastSavedEnabled)
        params.lastSavedEnabled = this.lastSavedEnabled;

      if (this.collapseSyndicated)
        params.collapse_syndicated = this.collapseSyndicated;

      if (this.bundleBroadcast) params.bundle_broadcast = this.bundleBroadcast;

      if (this.tiers.length) {
        const selectedTiers = this.tiers.map((tier) => tier.value);

        if (this.media.length > 0) {
          this.media.forEach((medium) => {
            params[`${snakeCase(medium) as MediumField}_tiers`] = selectedTiers;
          });
        } else if (this.stream !== null) {
          this.stream.enabled_media?.forEach((medium) => {
            params[`${snakeCase(medium) as MediumField}_tiers`] = selectedTiers;
          });
        }
      }

      if (
        this.stream?.type === streamTypes.bookmarkStream &&
        this.includeBookmarkSyndication
      ) {
        params.include_bookmark_syndication = this.includeBookmarkSyndication;
      }

      if (this.programs.length) params.programs = this.programs;

      if (this.frontPageRanksEnabled)
        params.front_page_ranks = this.frontPageRanks;
    }

    if (isStreamReportFilters(type, params)) {
      if (this.platforms.length > 0) {
        params.types = this.platformsToMediaTypes(this.platforms);
      }

      if (this.collapseSyndicated)
        params.collapse_syndicated = this.collapseSyndicated;
      if (this.minimumFollowerCountEnabled)
        params.minimum_follower_count = this.minimumFollowerCount;
      if (this.verifiedEnabled) params.verified = this.verifiedEnabled;
    }

    if (this.domainAuthorityRanges.length) {
      params.domain_authority = this.domainAuthorityRanges;
    }

    if (this.authors.length) {
      params.authors = this.authors
        .map((author) => (typeof author === "string" ? author : author.name))
        .join(", ");
    }

    if (this.excludedAuthors.length) {
      params.excluded_authors = this.excludedAuthors
        .map((author) => (typeof author === "string" ? author : author.name))
        .join(", ");
    }

    if (this.wordCountEnabled && this.validateMinOrMax(this.wordCount)) {
      if (this.wordCount.min !== null)
        params.min_word_count = this.wordCount.min;
      if (this.wordCount.max !== null)
        params.max_word_count = this.wordCount.max;
    }

    if (this.canFilterExternalItems()) {
      params.include_external_items = this.externalItemsEnabled;

      if (this.externalItemsEnabled) {
        params.only_external_items = this.onlyShowExternalItems;
      }
    }

    params.include_newswire_content = this.includeNewswireContent;

    if (this.locationKeys.length) {
      params.location_keys = this.locationKeys;
    }

    if (this.excludedLocationKeys.length) {
      params.excluded_location_keys = this.excludedLocationKeys;
    }

    if (this.sentimentEnabled) {
      params.sentiment = this.sentiment;
    }

    if (this.unratedSentiment) {
      params.unrated_sentiment = this.unratedSentiment;
    }

    if (this.media.length > 0) params.media = this.media;

    if (this.categoryKeys.length) {
      params.category_keys = this.categoryKeys;
    }

    if (this.pageNumbersEnabled && this.validateMinOrMax(this.pageNumbers)) {
      params.page_numbers = this.pageNumbers;
    }

    if (
      this.stream &&
      this.stream.type !== streamTypes.socialStream &&
      this.filteringReactScore()
    ) {
      if (
        this.reactScoreAverageSelected &&
        this.validateMinMax(this.reactScore)
      ) {
        params.min_react_score = this.reactScore.min / 100;
        params.max_react_score = this.reactScore.max / 100;
      } else {
        if (this.validateMinMax(this.aggregateEmotionality)) {
          params.min_aggregate_emotionality =
            this.aggregateEmotionality.min / 100;

          params.max_aggregate_emotionality =
            this.aggregateEmotionality.max / 100;
        }

        if (this.validateMinMax(this.aggregateFakeness)) {
          params.min_aggregate_fakeness = this.aggregateFakeness.min / 100;
          params.max_aggregate_fakeness = this.aggregateFakeness.max / 100;
        }

        if (this.validateMinMax(this.aggregateSpam)) {
          params.min_aggregate_spam = this.aggregateSpam.min / 100;
          params.max_aggregate_spam = this.aggregateSpam.max / 100;
        }

        if (this.validateMinMax(this.aggregateHarmful)) {
          params.min_aggregate_harmful = this.aggregateHarmful.min / 100;
          params.max_aggregate_harmful = this.aggregateHarmful.max / 100;
        }
      }
    }

    if (this.languageCodes.length) {
      params.language_codes = this.languageCodes;
    }

    if (this.excludedLanguageCodes.length) {
      params.excluded_language_codes = this.excludedLanguageCodes;
    }

    if (this.impactScoreRanges.length) {
      params.impact_score = this.impactScoreRanges;
    }

    if (this.validateMinOrMax(this.advertisementRate)) {
      if (this.advertisementRate.min !== null)
        params.min_advertisement_rate = this.advertisementRate.min * 100;
      if (this.advertisementRate.max !== null)
        params.max_advertisement_rate = this.advertisementRate.max * 100;
    }

    if (this.validateMinOrMax(this.audience)) {
      if (this.audience.min !== null) params.min_audience = this.audience.min;
      if (this.audience.max !== null) params.max_audience = this.audience.max;
    }

    if (this.validateMinOrMax(this.desktopMonthlyVisits)) {
      if (this.desktopMonthlyVisits.min !== null)
        params.min_desktop_monthly_visits = this.desktopMonthlyVisits.min;
      if (this.desktopMonthlyVisits.max !== null)
        params.max_desktop_monthly_visits = this.desktopMonthlyVisits.max;
    }

    if (this.validateMinOrMax(this.mobileMonthlyVisits)) {
      if (this.mobileMonthlyVisits.min !== null)
        params.min_mobile_monthly_visits = this.mobileMonthlyVisits.min;
      if (this.mobileMonthlyVisits.max !== null)
        params.max_mobile_monthly_visits = this.mobileMonthlyVisits.max;
    }

    if (this.license.length) {
      params.license = this.license;
    }

    if (this.excludedLicense.length) {
      params.excluded_license = this.excludedLicense;
    }

    if (this.includeInternationalPlus !== null) {
      params.include_international_plus = this.includeInternationalPlus;
    }

    if (this.includeOnlineCaldSources) {
      params.include_online_cald_sources = this.includeOnlineCaldSources;
    }

    if (this.includePrintCaldSources) {
      params.include_print_cald_sources = this.includePrintCaldSources;
    }

    if (this.selectedTvContent.length) {
      params.selected_tv_content = this.selectedTvContent;
    }

    if (this.selectedRadioContent.length) {
      params.selected_radio_content = this.selectedRadioContent;
    }

    if (this.selectedSocialContent.length) {
      params.selected_social_content = this.selectedSocialContent;
    }

    if (this.validateMinOrMax(this.socialImpactThreshold)) {
      if (this.socialImpactThreshold.min !== null)
        params.min_social_impact_threshold = this.socialImpactThreshold.min;
      if (this.socialImpactThreshold.max !== null)
        params.max_social_impact_threshold = this.socialImpactThreshold.max;
    }

    if (this.socialOnlyVerified !== null) {
      params.social_only_verified = this.socialOnlyVerified;
    }

    if (this.referredUrlsCache) {
      params.referred_urls_cache = this.referredUrlsCache;
    }

    if (this.shareTypes?.length) {
      params.share_types = this.shareTypes;
    }

    if (this.clusterTopicIds.length) {
      params.cluster_topic_ids = this.clusterTopicIds;
    }

    if (this.excludedClusterTopicIds.length) {
      params.excluded_cluster_topic_ids = this.excludedClusterTopicIds;
    }

    return params;
  }

  scoreChanged(score: StreamFiltersMinMax): boolean {
    return score.min !== 0 || score.max !== 100;
  }

  sourceFilters(): string[] {
    const filters: string[] = [];
    if (this.sources.length) filters.push("Included Sources");
    if (this.excludedSources.length) filters.push("Excluded Sources");

    return filters;
  }

  sourcesFiltersCount(): number {
    return this.sources.length + this.excludedSources.length;
  }

  supportsExternalItems(stream: Nullable<Stream>): boolean {
    if (!isNonNullable(stream)) return false;

    return [
      streamTypes.mentionStream,
      streamTypes.bookmarkStream,
      streamTypes.organisationBrief,
    ].includes(stream.type as number);
  }

  toOptions<T extends StreamFiltersType>(type?: T): StreamOptionTypes<T> {
    const options = <StreamOptionTypes<T>>{};

    if (this.authors.length) {
      const authorsFilter: StreamFiltersAuthor[] = [];

      this.authors.forEach((author) => {
        if (isStreamFiltersAuthor(author)) {
          authorsFilter.push(author);
        }
      });

      if (authorsFilter.length) {
        options.authors_filter = authorsFilter;
      }
    }

    if (this.excludedAuthors.length) {
      const excludedAuthorsFilter: StreamFiltersAuthor[] = [];

      this.excludedAuthors.forEach((author) => {
        if (isStreamFiltersAuthor(author)) {
          excludedAuthorsFilter.push(author);
        }
      });

      if (excludedAuthorsFilter.length) {
        options.excluded_authors_filter = excludedAuthorsFilter;
      }
    }

    if (this.simpleBoolean) {
      options.simple_boolean = this.simpleBoolean;
    }

    if (type === StreamFiltersType.Report) {
      const filters = this.requestFilters<StreamFiltersType.Report>(type);

      return {
        ...options,
        ...filters,
      };
    }

    const filters = this.requestFilters<StreamFiltersType.Stream>(
      StreamFiltersType.Stream
    );

    return {
      ...options,
      ...filters,
    };
  }

  totalFilters(): number {
    return (
      this.contentFiltersCount() +
      this.dateRangeFiltersCount() +
      this.keywordsFiltersCount() +
      this.mediaFiltersCount() +
      this.metricFiltersCount() +
      this.otherFiltersCount() +
      this.platformFiltersCount() +
      this.reportsFiltersCount() +
      this.sourcesFiltersCount()
    );
  }

  validateMinMax(
    minMax: StreamFiltersMinMax
  ): minMax is { [K in keyof StreamFiltersMinMax]: number } {
    return minMax.max !== null && minMax.min !== null;
  }

  validateMinOrMax(
    minMax: StreamFiltersMinMax
  ): minMax is { [K in keyof StreamFiltersMinMax]: number } {
    return minMax.max !== null || minMax.min !== null;
  }
}
